import React from "react";
import styled from "styled-components";
import { colors } from "../styles/GlobalStyles";
import { rv, mediaQueries, containerWidth } from "../styles/responsive";
import { Section, SectionLabel, SectionTitle } from "./ui";
import {
  DatabaseIcon,
  CpuIcon,
  CodeIcon,
  MobileIcon,
  GamepadIcon,
  DesktopIcon,
  BrainIcon,
} from "./ui/Icons";
import { ServiceData, ServiceInfo } from "../types/MainTypes";
import useFetchJson from "../hooks/useFetchJson";

const ServicesSection = styled(Section)`
  background-color: ${colors.white};
  padding: ${rv(60, 40)} 0;
  justify-content: center;
  min-height: 100vh;
`;

const ServicesContainer = styled.div`
  width: 100%;
  max-width: ${containerWidth(1280, 24)};
  margin: 0 auto;
  padding: 0 ${rv(24, 20)};
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const HeaderArea = styled.div`
  text-align: center;
  margin-bottom: ${rv(40, 28)};
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rv(16, 14)};
  width: 100%;

  ${mediaQueries.tabletUp} {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ServiceCard = styled.div`
  background: ${colors.slate[50]};
  border-radius: ${rv(16, 12)};
  padding: ${rv(24, 20)};
  display: flex;
  flex-direction: column;
  gap: ${rv(14, 12)};
  transition: all 0.3s ease;
  border: 1px solid transparent;
  height: 100%;

  &:hover {
    background: ${colors.white};
    border-color: ${colors.slate[200]};
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
    transform: translateY(-2px);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${rv(12, 10)};
`;

const IconBox = styled.div<{ $bgColor: string }>`
  width: ${rv(44, 40)};
  height: ${rv(44, 40)};
  border-radius: ${rv(10, 8)};
  background-color: ${({ $bgColor }) => $bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: ${rv(22, 20)};
    height: ${rv(22, 20)};
  }
`;

const CardTitle = styled.h3`
  font-size: ${rv(17, 15)};
  font-weight: 700;
  color: ${colors.slate[900]};
  letter-spacing: -0.01em;
`;

const CardDescription = styled.p`
  font-size: ${rv(14, 12)};
  color: ${colors.slate[500]};
  line-height: 1.65;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const getIconByName = (iconName: string) => {
  const iconMap: Record<string, React.ReactNode> = {
    "fa-solid fa-microchip": <BrainIcon color={colors.accent.indigo} />,
    "fa-sharp fa-solid fa-code": <CodeIcon color={colors.accent.blue} />,
    "fa-solid fa-server": <DatabaseIcon color={colors.accent.green} />,
    "fa-solid fa-gamepad": <GamepadIcon color="#ec4899" />,
    "fa-solid fa-tablet-screen-button": (
      <MobileIcon color={colors.accent.purple} />
    ),
    "fa-solid fa-desktop": <DesktopIcon color="#f59e0b" />,
  };
  return iconMap[iconName] || <CpuIcon color={colors.accent.indigo} />;
};

const getIconBgColor = (iconName: string) => {
  const colorMap: Record<string, string> = {
    "fa-solid fa-microchip": colors.brand[50],
    "fa-sharp fa-solid fa-code": "#dbeafe",
    "fa-solid fa-server": "#dcfce7",
    "fa-solid fa-gamepad": "#fce7f3",
    "fa-solid fa-tablet-screen-button": "#f3e8ff",
    "fa-solid fa-desktop": "#fef3c7",
  };
  return colorMap[iconName] || colors.brand[50];
};

const Services: React.FC = () => {
  const {
    data: serviceData,
    loading,
    error,
  } = useFetchJson<ServiceData>("service.json");

  if (loading) return null;
  if (error) return null;
  if (!serviceData) return null;

  const getFirstParagraph = (description: string) => {
    const firstParagraph = description.split("\n\n")[0];
    return firstParagraph;
  };

  return (
    <ServicesSection id="services">
      <ServicesContainer>
        <HeaderArea>
          <SectionLabel>Our Expertise</SectionLabel>
          <SectionTitle>AI-Powered Solutions</SectionTitle>
        </HeaderArea>

        <ServiceGrid>
          {serviceData.serviceList.map(
            (service: ServiceInfo, index: number) => (
              <ServiceCard key={index}>
                <CardHeader>
                  <IconBox $bgColor={getIconBgColor(service.icon)}>
                    {getIconByName(service.icon)}
                  </IconBox>
                  <CardTitle>{service.title}</CardTitle>
                </CardHeader>
                <CardDescription>
                  {getFirstParagraph(service.description)}
                </CardDescription>
              </ServiceCard>
            ),
          )}
        </ServiceGrid>
      </ServicesContainer>
    </ServicesSection>
  );
};

export default Services;
